import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Transition } from 'react-transition-group'

import { DataContext } from '@aytm/app/stats/page/sidebar/coding/CodingPanel'

import useSingleAndDoubleClick from '@aytm/app/stats/page/components/hooks/useSingleAndDoubleClick'

import Popup, { PopupCloseButton } from '@aytm/lib/rfd-ui/components/basic/popup'

import CodingTagSentiment from '@aytm/app/stats/page/sidebar/coding/CodingTagSentiment'
import CodebookSettingsSentiment from '@aytm/app/stats/page/sidebar/coding/components/CodebookSettingsSentiment'

import Sentiments from '@aytm/app/stats/page/sidebar/coding/components/Sentiments'

import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

let dndTimer = null

const CodingCodebookSentiment = ({
  codebook,
  error,
  message,
  progress,
  processingTag,
  settingsItem,
  collapsedRefs,
  filteredQuestions,
  isProcessing,
  isCurrentProcessing
}) => {
  const {
    survey,
    sentiments,
    features,
    tagsList,
    isSentimentsDisabled,
    onCollapse,
    onMessage,
    onCodebookConfirm,
    onSentimentsChange
  } = useContext(DataContext)

  const { questions } = survey

  const [collapsed, setCollapsed] = useState(
    Object.hasOwn(collapsedRefs.current, codebook.codebook) ? collapsedRefs.current[codebook.codebook] : true
  )

  const [transitionDuration, setTransitionDuration] = useState(500)

  const codebookRef = useRef(null)

  const componentRef = useRef(null)

  const collapsedRef = useRef()
  collapsedRef.current = collapsed

  const isDragging = useRef(false)

  const isOpened = useMemo(
    () =>
      (settingsItem &&
        settingsItem.item == codebook.codebook &&
        settingsItem.type == codebook.type &&
        settingsItem.codebook === undefined) ||
      false,
    [codebook, settingsItem]
  )

  const tags = useMemo(() => {
    const _tags = codebook.tags.reduce((acc, cur) => {
      const _tag = tagsList.find(tag => cur == tag.tag)
      return _tag && null !== _tag ? acc.concat(_tag) : acc
    }, [])
    return _tags
  }, [codebook, tagsList])

  const onDNDStartTag = () => {
    isDragging.current = true
    dispatchEvent(new CustomEvent('dndStartTag'))
  }
  const onDNDEndTag = () => {
    dndTimer && clearTimeout(dndTimer)
    dndTimer = setTimeout(() => {
      isDragging.current = false
    }, 300)
    dispatchEvent(new CustomEvent('dndEndTag'))
  }

  const onToggle = () => {
    setCollapsed(!collapsedRef.current)
    setTransitionDuration(500)
  }

  const onActionConfirm = useCallback(action => onCodebookConfirm(action, codebook), [codebook, onCodebookConfirm])
  const addCommentsSearchOption = useCallback(
    () => dispatchEvent(new CustomEvent('addCommentsSearchOption', { detail: { item: codebook } })),
    [codebook]
  )

  const onTitleClick = useSingleAndDoubleClick(onToggle, isProcessing ? null : addCommentsSearchOption)

  useEffect(() => {
    setTransitionDuration(collapsed ? 500 : 10)

    onCollapse(codebook.codebook, collapsed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, codebook.codebook])

  return (
    <div
      className={classNames(style.codebook, {
        [style['codebook-analysing']]: isCurrentProcessing
      })}
    >
      <div
        className={classNames(style['codebook-header'], {
          [style['codebook-header-collapsed']]: collapsed
        })}
      >
        {0 < message?.text?.length && message.item == codebook.codebook ? (
          <Popup
            className={style['component-popup']}
            width="250px"
            trigger="none"
            direction="bottomleft"
            show
            hideOtherPopupsOnShow={false}
            content={() => (
              <>
                <PopupCloseButton className={style['component-popup-close']} onClose={onMessage}>
                  <i
                    data-testid={`stat-coding-codebook-sentiment-close-${codebook.codebook}`}
                    className="icon-aytmfd-x-v2"
                  />
                </PopupCloseButton>
                <div
                  className={classNames(style['component-popup-content'], {
                    [style['component-popup-content-failed']]: 'error' == message.type,
                    [style['component-popup-content-success']]: 'error' != message.type
                  })}
                >
                  {'error' == message.type ? (
                    <span className={style['component-popup-icon']}>🧭</span>
                  ) : (
                    <span className={style['component-popup-icon']}>🙌</span>
                  )}
                  <span dangerouslySetInnerHTML={{ __html: message.text }}></span>
                </div>
              </>
            )}
          >
            <div className={`${style['component-popup-anchor']} rfd-ui-popup-target-anchor`} />
          </Popup>
        ) : null}

        {isCurrentProcessing ? (
          <span className={style['codebook-header-fill']} style={{ width: `${progress}%` }}></span>
        ) : null}

        <div
          ref={codebookRef}
          data-testid={`stat-coding-codebook-sentiment-title-${codebook.codebook}`}
          className={style['codebook-subheader']}
          onClick={onTitleClick}
        >
          <span className={style['codebook-header-icon']}>
            <i
              className={collapsed ? 'icon-aytmfd-folder-closed' : 'icon-aytmfd-folder-opened'}
              style={{ color: codebook.color }}
            />
          </span>
          <span className={style['codebook-header-title']}>
            <span dangerouslySetInnerHTML={{ __html: codebook.codebook }} />
          </span>
        </div>

        <span
          data-testid={`stat-coding-codebook-sentiment-toggle-${codebook.codebook}`}
          className={style['codebook-header-toggle']}
          aria-label="Show codes"
          aria-expanded={!collapsed}
          {...pressable(onToggle)}
        >
          <i className="icon-aytmfd-chevron-bracket" />
        </span>

        {features.oe_sentiments_management.disabled ? null : (
          <CodebookSettingsSentiment
            codebookRef={codebookRef}
            codebook={codebook}
            opened={isOpened}
            isProcessing={isProcessing}
            isCurrentProcessing={isCurrentProcessing}
            onConfirm={onActionConfirm}
          />
        )}
      </div>

      {error && error.message && codebook.codebook == error.codebook && !Object.hasOwn(error, 'tag') && (
        <div className={style['component-error']}>{error.message}</div>
      )}

      <Transition in={!collapsed} timeout={transitionDuration}>
        {status => (
          <div
            className={`${style['transition-panel']}${status ? ' ' + style['transition-panel-' + status] : ''}`}
            style={{ transitionDuration: `${transitionDuration}ms` }}
          >
            <div ref={componentRef} className={style['codebook-body']}>
              {isSentimentsDisabled ? (
                <div className={style['component-notification']}>no questions for sentiment analysis</div>
              ) : null}

              {0 < tags.length ? (
                <div className={style['codebook-items']}>
                  {tags.map((tag, index) => (
                    <CodingTagSentiment
                      key={`tag-${tag.type}-${tag.tag}-${index + 1}`}
                      tag={tag}
                      error={error}
                      settingsItem={settingsItem}
                      isProcessing={isProcessing}
                      isCurrentProcessing={
                        isProcessing && processingTag.current && processingTag.current.tag == tag.tag
                      }
                      onDNDStart={onDNDStartTag}
                      onDNDEnd={onDNDEndTag}
                    />
                  ))}
                </div>
              ) : null}

              {!features.oe_sentiments_management.disabled && features.ai_assistants_stat_llm_sentiments.disabled ? (
                <Sentiments
                  questions={questions}
                  sentiments={sentiments}
                  features={features}
                  filteredQuestions={filteredQuestions}
                  onSentimentsChange={onSentimentsChange}
                />
              ) : null}
            </div>
          </div>
        )}
      </Transition>
    </div>
  )
}

CodingCodebookSentiment.propTypes = {
  codebook: PropTypes.object,
  error: PropTypes.object,
  message: PropTypes.object,
  progress: PropTypes.number,
  processingTag: PropTypes.object,
  settingsItem: PropTypes.object,
  collapsedRefs: PropTypes.any,
  filteredQuestions: PropTypes.array,
  isProcessing: PropTypes.bool,
  isCurrentProcessing: PropTypes.bool,
  onSentimentsChange: PropTypes.func
}

CodingCodebookSentiment.displayName = 'CodingCodebookSentiment'

export default CodingCodebookSentiment
